'use client';

import { useState } from 'react';
import { useParams } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';

import Button from '@/components/ui/Button';
import FormFieldRenderer from '@/components/ui/FormFieldRenderer';
import Text from '@/components/ui/Text';
import type { FormConfig, FormField } from '@/lib/contentful/types';
import { submitContactForm } from '@/lib/contact/submitContactForm';
import { CONTACT_FORM_I18N, normalizeLocale } from '@/lib/i18n/contactFormI18n';

const ContactForm = ({ config, fields }: ContactFormProps) => {
  const { locale: rawLocale } = useParams<{ locale: string }>();
  const locale = normalizeLocale(rawLocale);

  const { copy, validation } = CONTACT_FORM_I18N[locale];

  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: Object.fromEntries(fields.map((f) => [f.name, ''])),
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    setSubmitError(null);
    setIsSuccess(false);

    try {
      const name = pickByValidationType(values, fields, 'name');
      const email = pickByValidationType(values, fields, 'email');
      const phone = pickByValidationType(values, fields, 'phone');
      const message = pickMessage(values, fields);

      await submitContactForm(locale, { name, email, phone, message });

      window.dataLayer?.push({
        event: 'generate_lead',
        form: 'contact',
      });
      methods.reset();
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      setSubmitError(copy.submitError);
    }
  });

  return (
    <FormProvider {...methods}>
      <form className="space-y-3" onSubmit={onSubmit}>
        {fields.map((field) => (
          <FormFieldRenderer
            key={field.name}
            field={field}
            validationMessages={validation}
          />
        ))}

        {config?.consentText && (
          <Text as="p" variant="formConsent">
            {config.consentText}
          </Text>
        )}

        <Button
          type="submit"
          variant="secondary"
          className="w-full"
          disabled={
            methods.formState.isSubmitting || !methods.formState.isValid
          }
        >
          {methods.formState.isSubmitting ? copy.sending : copy.button}
        </Button>

        {isSuccess && (
          <div
            role="alert"
            className="animate-fade-in p-4 rounded-xl border border-green-200 bg-green-50"
          >
            <Text as="p" variant="formConsent">
              {copy.success}
            </Text>
          </div>
        )}

        {submitError && (
          <div
            role="alert"
            className="animate-fade-in p-4 rounded-xl border border-red-200 bg-red-50"
          >
            <Text as="p" variant="formConsent" className="text-red-700">
              {submitError}
            </Text>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default ContactForm;

type ContactFormProps = {
  config?: FormConfig;
  fields: FormField[];
};

type FormValues = Record<string, string>;

function pickByValidationType(
  values: Record<string, string>,
  fields: FormField[],
  type: FormField['validationType'],
) {
  const field = fields.find((f) => f.validationType === type);
  return field ? (values[field.name] ?? '').trim() : '';
}

function pickMessage(values: Record<string, string>, fields: FormField[]) {
  const field = fields.find((f) => f.inputType === 'textarea');
  return field ? (values[field.name] ?? '').trim() : '';
}
